import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, ReferenceArea,
} from 'recharts';
import { useBreadcrumb, useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import AlertActions, { useConfirmDelete } from 'features/alerts/components/AlertActions';
import BrandLoadingSpinner from 'shared/components/ui/BrandLoadingSpinner';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import { presetToISORange } from 'shared/utils/timePresets';
import TableFooter from 'shared/components/tables/TableFooter';
import { getAlert, getAlertCatalog, getAlertSeries, getAlertStats, listAlertEvents, setAlertEnabled, deleteAlert } from 'features/alerts/api/alertsApi';
import { formatValue, timeAgo } from 'features/alerts/format';
import { tableHeaderRowClass, tableHeaderCellClass, tableDataCellClass } from 'features/activity/styles/dataTableStyles';

/**
 * What an alert has been doing: the number over time, the fires, the history.
 * Drawn the way an eval's Analysis is drawn — tiles, then cards with a title
 * and their controls on the right — so the two read as one product.
 */

const CARD = 'min-w-0 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)]';

/** One figure, with the line under it that says what it is. */
function Tile({ label, children, sub, color, className = '' }) {
  return (
    <div className={`${CARD} px-3 py-2.5 ${className}`}>
      <div className="truncate text-[12px] font-medium text-[color:var(--text-primary)]">{label}</div>
      <div className="mt-0.5 truncate text-[20px] font-semibold leading-tight tabular-nums" style={{ color: color || 'var(--text-primary)' }}>{children}</div>
      {sub ? <div className="mt-0.5 truncate text-[11px] text-[color:var(--text-muted)]">{sub}</div> : null}
    </div>
  );
}

/** A card: its title and what it is on one row, then what it shows. */
function Card({ title, sub, children, className = '', flush = false }) {
  return (
    <section className={`${CARD} flex flex-col ${flush ? 'overflow-hidden' : ''} ${className}`}>
      <header className="flex min-h-[32px] items-center gap-2 px-3 pt-2.5">
        <h3 className="truncate text-[12px] font-medium text-[color:var(--text-primary)]">{title}</h3>
        {sub ? <span className="truncate text-[11px] text-[color:var(--text-muted)]">{sub}</span> : null}
      </header>
      <div className={flush ? 'min-h-0 flex-1 pt-1' : 'min-h-0 flex-1 px-3 pb-3 pt-1'}>{children}</div>
    </section>
  );
}

const CHANNEL = { slack: 'Slack', webhook: 'Webhook' };

/** The "Sent to" text for one fire: where it was delivered, and what its agents did. */
function sentTo(ev) {
  const d = ev.delivery && typeof ev.delivery === 'object' ? ev.delivery : null;
  const chans = d ? Object.keys(d).filter((c) => c !== 'agents') : [];
  const delivered = chans.map((c) => CHANNEL[c] || c).join(', ');
  const failedDelivery = Boolean(ev.error || (d && chans.some((c) => d[c] && d[c].ok === false)));
  const agents = Array.isArray(d?.agents) ? d.agents : [];
  const skipped = agents.filter((a) => a.skipped);
  const errored = agents.filter((a) => a.error);
  const ran = agents.length - skipped.length - errored.length;
  const parts = [];
  if (delivered || !agents.length) parts.push(failedDelivery ? `Not delivered · ${delivered || '—'}` : (delivered || '—'));
  else if (failedDelivery) parts.push('Not delivered');
  if (ran) parts.push(ran === 1 ? 'Agent' : `${ran} agents`);
  if (skipped.length) parts.push(`${skipped.length === 1 ? 'Agent' : `${skipped.length} agents`} not run: ${String(skipped[0].reason || 'skipped').toLowerCase()}`);
  if (errored.length) parts.push(`${errored.length === 1 ? 'Agent' : `${errored.length} agents`} failed to start`);
  return { text: parts.join(' · '), failed: failedDelivery || errored.length > 0 };
}

/** The one time period, the app's own range picker. Alerts keep 30 days of readings, so that is the default. */
const DEFAULT_PRESET = '30d';
const DAY = 86400000;
const PAGE = 10;
const rangeLabel = (days) => (days <= 1.01 ? 'last 24 hours' : `last ${Math.round(days)} days`);

const pad2 = (n) => String(n).padStart(2, '0');
/** A viewer-local bucket start, in the form the server returns when it is given the viewer's time zone. */
const localKey = (d, bucket) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${bucket === 'hour' ? pad2(d.getHours()) : '00'}:00:00`;
const axis = { fontSize: 10, fill: 'var(--text-muted)' };
const tooltipStyle = {
  contentStyle: { background: 'var(--surface-base)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 11 },
  labelStyle: { color: 'var(--text-muted)' },
  itemStyle: { color: 'var(--text-primary)' },
};

export default function AlertDetailPage({ alertId }) {
  const { team } = useParams();
  const navigate = useNavigate();
  const confirmDelete = useConfirmDelete();

  const [alert, setAlert] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [series, setSeries] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [period, setPeriod] = useState({ preset: DEFAULT_PRESET, from: null, to: null });
  const [shown, setShown] = useState(PAGE);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([getAlert(alertId), getAlertCatalog()])
      .then(([a, c]) => { if (!cancelled) { setAlert(a); setCatalog(c); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [alertId]);

  // The one period drives the numbers, the chart, the bars and the table.
  const win = useMemo(() => {
    const r = period.preset === 'custom' && period.from && period.to
      ? { from: period.from, to: period.to }
      : presetToISORange(period.preset) || presetToISORange(DEFAULT_PRESET);
    const from = Date.parse(r.from);
    const to = Date.parse(r.to);
    return { from, to, days: Math.max((to - from) / DAY, 1 / 24) };
  }, [period, alertId]); // eslint-disable-line react-hooks/exhaustive-deps
  const days = win.days;
  useEffect(() => {
    let cancelled = false;
    const range = { from: new Date(win.from).toISOString(), to: new Date(win.to).toISOString() };
    setShown(PAGE);
    Promise.all([getAlertStats(alertId, range), listAlertEvents(alertId, range), getAlertSeries(alertId, range)])
      .then(([st, ev, se]) => { if (!cancelled) { setStats(st); setEvents(ev.events); setEventsTotal(ev.total); setSeries(se); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [alertId, win]);

  // Show more rows from what is loaded; fetch the next page only when the
  // loaded ones run out.
  const loadMore = async () => {
    const next = shown + PAGE;
    if (next > events.length && events.length < eventsTotal) {
      setLoadingMore(true);
      try {
        const range = { from: new Date(win.from).toISOString(), to: new Date(win.to).toISOString() };
        const more = await listAlertEvents(alertId, range, events.length);
        setEvents((cur) => [...cur, ...more.events]);
        setEventsTotal(more.total);
      } catch (e) { setError(e?.response?.data?.error || e.message); }
      setLoadingMore(false);
    }
    setShown(next);
  };

  const toggle = async () => setAlert(await setAlertEnabled(alertId, !alert.enabled));
  const remove = async () => {
    if (!(await confirmDelete(alert))) return;
    await deleteAlert(alertId);
    navigate(`/${team}/alerts`);
  };

  // Path and controls in the app's own top bar, like every other page. The
  // state is the first tile on the page, so the bar carries only actions.
  // `null` while loading claims nothing.
  const headState = alert ? (alert.enabled ? alert.state : 'paused') : null; // 'alerting' only colours Current
  useBreadcrumb(useMemo(() => [
    { label: 'Alerts', to: `/${team}/alerts` },
    { label: alert?.name || '…' },
  ], [team, alert]));

  useBreadcrumbActions(
    alert ? (
      <div className="flex items-center gap-2">
        <EventsTimeFilter selectedPreset={period.preset} menuAlign="right"
          onTimeFilterChange={(n) => setPeriod({ preset: n.preset || 'custom', from: n.from, to: n.to })} />
        <AlertActions alert={alert} onEdit={() => navigate(`/${team}/alerts/${alertId}/edit`)} onToggle={toggle} onDelete={remove} />
      </div>
    ) : null,
  );

  if (error) return <div className="p-6 text-[12px] text-[color:var(--feedback-error)]">{error}</div>;
  if (!alert) return <div className="flex h-full items-center justify-center"><BrandLoadingSpinner message="Loading alert" /></div>;

  const unit = alert.unit;

  // The chart's title is what the alert measures, in the catalog's own words —
  // "Duration p95", not "duration". `count` and `rate` are already the whole
  // name, so they do not get a suffix.
  const measureLabel = (() => {
    const m = (catalog?.measures?.[alert.scope] || []).find((x) => x.key === alert.measure);
    const base = m?.label || alert.measure;
    if (['count', 'rate'].includes(alert.aggregation)) return base;
    return `${base} ${catalog?.aggregations?.[alert.aggregation] || alert.aggregation}`;
  })();
  const breachAbove = alert.operator === 'above' || alert.operator === 'at_or_above';

  const longRange = days > 1.01;
  const fmtTime = (t, i) => {
    const d = new Date(t);
    if (longRange) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    // A day's ends are the same clock time: the first tick says which day it starts on.
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return i === 0 ? `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}` : time;
  };
  // Evenly spaced ticks over the whole period, so the axis shows the period and not just where data happens to be.
  const xTicks = Array.from({ length: 6 }, (_, i) => Math.round(win.from + ((win.to - win.from) * i) / 5));
  const points = series.map((p) => ({ ...p, ts: new Date(p.t).getTime() }));

  /**
   * Keep the threshold on screen. Without this a measure sitting far below its
   * threshold draws a flat line with the threshold cropped off the top, and
   * the chart stops answering "how close is this".
   *
   * The floor stays at zero for anything that cannot be negative — a duration
   * axis starting at -4 seconds is not a reading, it is padding.
   */
  const canBeNegative = !['ms', 'count', 'usd', 'percent'].includes(unit);
  const yDomain = (() => {
    const vals = series.map((p) => p.value).filter((v) => v != null);
    const t = Number(alert.threshold);
    const floor = (v) => (canBeNegative ? v : Math.max(0, v));
    if (!vals.length) return [floor(Math.min(0, t)), Math.max(t * 1.2, 1)];
    const lo = Math.min(...vals, t);
    const hi = Math.max(...vals, t);
    const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.12 || 1;
    return [floor(lo - pad), hi + pad];
  })();

  /** Where the vertical fire markers go. */
  const firedAt = events.map((e) => new Date(e.fired_at).getTime()).filter((t) => t >= win.from && t <= win.to);

  // Fires per bucket, with the empty ones filled so the bars read as a calendar:
  // by hour for a day or two, by day beyond.
  const bucket = stats?.bucket || (days <= 2 ? 'hour' : 'day');
  const perBucket = (() => {
    const local = stats?.local;
    const key = (v) => (local ? String(v).slice(0, 19) : localKey(new Date(v), bucket));
    const map = new Map((stats?.per_day || []).map((r) => [key(r.day), r.count]));
    const step = bucket === 'hour' ? 3600000 : 86400000;
    const start = new Date(win.from);
    if (bucket === 'hour') start.setMinutes(0, 0, 0); else start.setHours(0, 0, 0, 0);
    const out = [];
    for (let d = new Date(start); d.getTime() <= win.to; d = new Date(bucket === 'hour' ? d.getTime() + step : new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime())) {
      out.push({
        at: d.getTime(),
        label: bucket === 'hour' ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        count: map.get(localKey(d, bucket)) || 0,
      });
    }
    return out;
  })();
  const inRange = stats?.in_range ?? 0;
  const lastValue = alert.last_value;
  const limitText = `${breachAbove ? 'above' : 'below'} ${formatValue(alert.threshold, unit)}`;

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <Tile label="Condition" className="sm:col-span-3 lg:col-span-1"
            sub={(
              <>
                <span className="capitalize">{alert.scope}</span>
                {alert.scope === 'eval' && (
                  <>
                    {' · '}
                    <a href={`/${encodeURIComponent(team)}/agent-analytics/evals?eval=${alert.eval_lineage_id}&filter=failed`}
                      className="text-[color:var(--trodo-primary)] hover:underline" title="Open this eval's failed results">
                      {alert.eval_name || 'Deleted eval'}
                    </a>
                  </>
                )}
                {headState === 'paused' && ' · Paused'}
              </>
            )}>
            <span className="block whitespace-normal text-[14px] font-medium leading-snug text-[color:var(--text-primary)]" title={alert.condition || measureLabel}>
              {alert.condition || measureLabel}
            </span>
          </Tile>
          <Tile label="Current" color={headState === 'alerting' ? 'var(--feedback-error)' : undefined}
            sub={`triggers ${limitText}`}>
            {lastValue == null ? '—' : formatValue(lastValue, unit)}
          </Tile>
          <Tile label="Triggered" sub={rangeLabel(days)}>{inRange}</Tile>
          <Tile label="Last triggered">{alert.last_triggered_at ? timeAgo(alert.last_triggered_at) : 'Never'}</Tile>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* The measure over the period, with the part that counts as a breach shaded. */}
        <Card className="lg:col-span-2" title={measureLabel} sub={`triggers ${limitText}`}>
          {points.length === 0 ? (
            <div className="flex h-[200px] items-center justify-center text-[12px] text-[color:var(--text-muted)]">
              No readings in this period
            </div>
          ) : (
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="ts" type="number" scale="time" domain={[win.from, win.to]} ticks={xTicks} tickFormatter={fmtTime}
                    tick={axis} axisLine={{ stroke: 'var(--border-default)' }} tickLine={false} />
                  <YAxis tick={axis} axisLine={false} tickLine={false} width={58} domain={yDomain} tickFormatter={(v) => formatValue(v, unit)} />
                  <Tooltip
                    {...tooltipStyle}
                    labelFormatter={(t) => new Date(t).toLocaleString()}
                    formatter={(v) => [formatValue(v, unit), measureLabel]}
                  />
                  <ReferenceArea
                    y1={breachAbove ? alert.threshold : yDomain[0]}
                    y2={breachAbove ? yDomain[1] : alert.threshold}
                    fill="var(--feedback-error)"
                    fillOpacity={0.07}
                  />
                  <ReferenceLine y={alert.threshold} stroke="var(--feedback-error)" strokeDasharray="4 3" strokeWidth={1}
                    label={{ value: `limit ${formatValue(alert.threshold, unit)}`, position: 'insideBottomRight', fontSize: 10, fill: 'var(--feedback-error)' }} />
                  <Area type="monotone" dataKey="value" stroke="none" fill="var(--trodo-primary)" fillOpacity={0.08} connectNulls isAnimationActive={false} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="value"
                    stroke="var(--trodo-primary)"
                    strokeWidth={1.5}
                    dot={points.length <= 12 ? { r: 2.5, fill: 'var(--trodo-primary)', strokeWidth: 0 } : false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  {firedAt.map((t) => (
                    <ReferenceLine key={t} x={t} stroke="var(--feedback-error)" strokeWidth={1} strokeOpacity={0.55} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Triggered" sub={bucket === 'hour' ? 'per hour' : 'per day'}>
          {inRange === 0 ? (
            <div className="flex h-[200px] items-center justify-center text-[12px] text-[color:var(--text-muted)]">
              Not triggered in this period
            </div>
          ) : (
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perBucket} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="label" tick={axis} axisLine={{ stroke: 'var(--border-default)' }} tickLine={false} minTickGap={30} />
                  <YAxis tick={axis} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: 'var(--surface-hover)' }}
                    labelFormatter={(_, p) => (p?.[0]?.payload?.at ? new Date(p[0].payload.at).toLocaleString([], bucket === 'hour' ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' }) : '')}
                    formatter={(v) => [v, 'Triggered']} />
                  <Bar dataKey="count" name="Triggered" fill="var(--feedback-error)" radius={[2, 2, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        </div>

        <Card className="mt-3" flush title="History">
        {events.length === 0 ? (
          <div className="px-3 pb-6 pt-4 text-center text-[12px] text-[color:var(--text-muted)]">
            Not triggered in this period
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left">
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th scope="col" className={`${tableHeaderCellClass} w-[140px]`}>When</th>
                    <th scope="col" className={tableHeaderCellClass}>Value</th>
                    <th scope="col" className={tableHeaderCellClass}>Sent to</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice(0, shown).map((ev) => {
                    const { text: channels, failed } = sentTo(ev);
                    return (
                      <tr key={ev.id} className="h-9 border-b border-[color:var(--border-subtle)] last:border-b-0">
                        <td className={`${tableDataCellClass} whitespace-nowrap text-[12px] text-[color:var(--text-primary)]`} title={new Date(ev.fired_at).toLocaleString()}>
                          {timeAgo(ev.fired_at)}
                        </td>
                        <td className={`${tableDataCellClass} whitespace-nowrap tabular-nums text-[12px]`}>
                          <span className="font-medium">{formatValue(ev.value, unit)}</span>
                          <span className="text-[color:var(--text-muted)]"> · limit {formatValue(ev.threshold, unit)}</span>
                        </td>
                        <td className={`${tableDataCellClass} text-[12px]`} style={{ color: failed ? 'var(--feedback-error)' : 'var(--text-secondary)' }}>
                          {channels}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {eventsTotal > PAGE && <TableFooter shown={Math.min(shown, events.length)} total={eventsTotal} hasMore={shown < eventsTotal} loading={loadingMore}
              onLoadMore={loadMore} className="border-t border-[color:var(--border-default)] px-3 pb-2" />}
          </div>
        )}
        </Card>
      </div>
    </div>
  );
}
